import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Lock, Truck, Check, Tag, X } from "lucide-react";
import { computeDeliverySlots } from "@/lib/deliverySlots";
import { API } from "@/auth/AuthContext";

const POPUP_STORAGE_KEY = "cmp_popup_state_v1";

function emailValid(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
function phoneValid(v) {
    return v.replace(/\D/g, "").length >= 10;
}
function prettyDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
    });
}

export default function CheckoutModal({
    open,
    onOpenChange,
    summary,
    onSubmit,
    loading,
    prefill,
    canSubscribe,
    initialSubscribe = false,
    onAuthRequired,
    onRemoveFamilyItem,
}) {
    const [step, setStep] = useState(1);
    const [subscribe, setSubscribe] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
        delivery_date: "",
        notes: "",
    });
    // Promo code state — independent of `form` so we can validate against backend.
    const [promoInput, setPromoInput] = useState("");
    const [promo, setPromo] = useState(null); // { code, percent_off } when validated
    const [promoBusy, setPromoBusy] = useState(false);
    const [promoErr, setPromoErr] = useState("");
    // Pre-order deposit plan
    const [depositPct, setDepositPct] = useState(100); // 100 = pay in full
    // Delivery quote (Woburn-anchored distance pricing)
    const [deliveryQuote, setDeliveryQuote] = useState(null); // {zone, distance_miles, delivery_fee, deliverable}
    const [deliveryBusy, setDeliveryBusy] = useState(false);
    const slots = useMemo(() => computeDeliverySlots(), [open]);

    // When the modal opens, try to auto-prefill the promo code from the
    // welcome-popup localStorage (so the customer doesn't have to copy/paste).
    useEffect(() => {
        if (!open) return;
        try {
            const raw = localStorage.getItem(POPUP_STORAGE_KEY);
            if (!raw) return;
            const s = JSON.parse(raw);
            if (s?.welcomeCode && !promoInput && !promo) {
                setPromoInput(s.welcomeCode);
            }
        } catch {
            /* ignore */
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            setStep(1);
            setPromo(null);
            setPromoInput("");
            setPromoErr("");
            setDeliveryQuote(null);
        } else {
            setSubscribe(canSubscribe && initialSubscribe);
            if (prefill) {
                setForm((f) => ({
                    ...f,
                    name: f.name || prefill.name || "",
                    email: f.email || prefill.email || "",
                    phone: f.phone || prefill.phone || "",
                }));
            }
        }
    }, [open, prefill, canSubscribe, initialSubscribe]);

    // Volume tier discount already computed in summary by CartProvider/backend.
    // Subscribe discount applies on top of any tier discount, on the full subtotal.
    const volumeDiscount = summary.volumeDiscount || 0;
    const subDiscount = subscribe
        ? +(summary.subtotal * 0.1).toFixed(2)
        : 0;
    // Debounced delivery-fee quote from the address (Woburn-anchored zones)
    useEffect(() => {
        const addr = (form.address || "").trim();
        if (!open || addr.length < 8) {
            setDeliveryQuote(null);
            return;
        }
        let cancelled = false;
        const handle = setTimeout(async () => {
            setDeliveryBusy(true);
            try {
                const r = await axios.post(`${API}/delivery/quote`, { address: addr });
                if (!cancelled) setDeliveryQuote(r.data);
            } catch {
                if (!cancelled) setDeliveryQuote(null);
            } finally {
                if (!cancelled) setDeliveryBusy(false);
            }
        }, 700);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [form.address, open]);

    // Resolved delivery fee: prefer the quoted fee, else fall back to the
    // base summary fee (covers the moment before quote arrives).
    const resolvedDeliveryFee = useMemo(() => {
        if (deliveryQuote?.deliverable === false) return 0;
        if (typeof deliveryQuote?.delivery_fee === "number") return deliveryQuote.delivery_fee;
        return summary.deliveryFee;
    }, [deliveryQuote, summary.deliveryFee]);

    const promoDiscount = promo
        ? +(summary.subtotal * (promo.percent_off / 100)).toFixed(2)
        : 0;
    const discount = +(volumeDiscount + subDiscount + promoDiscount).toFixed(2);
    const finalTotal = +(
        summary.subtotal - discount + (summary.count > 0 ? resolvedDeliveryFee : 0)
    ).toFixed(2);

    // Selected slot + pre-order detection — pulled from the slots picker
    const selectedSlot = useMemo(
        () => slots.find((s) => s.iso === form.delivery_date) || null,
        [slots, form.delivery_date],
    );
    const isPreOrder = !!selectedSlot?.isPreOrder;

    // When pre-order toggles off (e.g., picked a non-pre-order date), force full payment
    useEffect(() => {
        if (!isPreOrder) setDepositPct(100);
    }, [isPreOrder]);

    const depositAmount = isPreOrder
        ? +(finalTotal * (depositPct / 100)).toFixed(2)
        : finalTotal;
    const balanceAmount = +(finalTotal - depositAmount).toFixed(2);
    // Balance due: 10 PM ET the day before delivery
    const balanceDueText = useMemo(() => {
        if (!isPreOrder || !form.delivery_date) return "";
        const [y, m, d] = form.delivery_date.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() - 1);
        return dt.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
        }) + " · 10 PM ET";
    }, [isPreOrder, form.delivery_date]);

    const today = new Date();

    const formValid = useMemo(() => {
        return (
            form.name.trim().length >= 2 &&
            emailValid(form.email.trim()) &&
            phoneValid(form.phone) &&
            form.address.trim().length >= 6 &&
            !!form.delivery_date &&
            // If we have a quote and it's NOT deliverable, block. If quote not
            // yet returned, allow (backend will validate definitively).
            (deliveryQuote == null || deliveryQuote.deliverable !== false)
        );
    }, [form, deliveryQuote]);

    const upd = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSlotPick = (iso) => {
        setForm((f) => ({ ...f, delivery_date: iso }));
    };

    const goPay = () => {
        if (!formValid) return;
        onSubmit({
            ...form,
            subscribe,
            promo_code: promo ? promo.code : null,
            payment_plan: isPreOrder && depositPct < 100 ? "deposit" : "full",
            deposit_percent: isPreOrder && depositPct < 100 ? depositPct : 100,
        });
    };

    const applyPromo = async () => {
        const code = promoInput.trim().toUpperCase();
        const email = form.email.trim();
        setPromoErr("");
        if (!code) return;
        if (!emailValid(email)) {
            setPromoErr("Enter your email above first, then apply your code.");
            return;
        }
        setPromoBusy(true);
        try {
            const res = await axios.post(`${API}/marketing/validate-promo`, {
                code,
                email,
            });
            if (res.data?.valid) {
                setPromo({
                    code: res.data.code,
                    percent_off: res.data.percent_off,
                });
            } else {
                setPromo(null);
                setPromoErr(
                    "That code isn't valid for this email or has already been used.",
                );
            }
        } catch {
            setPromoErr("Couldn't check the code. Try again.");
        } finally {
            setPromoBusy(false);
        }
    };

    const clearPromo = () => {
        setPromo(null);
        setPromoInput("");
        setPromoErr("");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="checkout-modal"
                className="max-w-lg w-[calc(100vw-2rem)] bg-cream rounded-[24px] border-0 p-0 overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[88vh]"
            >
                <div className="p-6 sm:p-7 border-b border-mint bg-white shrink-0">
                    <DialogTitle className="font-serif font-black text-2xl text-navy">
                        {step === 1 ? "Delivery details" : "Review & pay"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted2 mt-1">
                        {step === 1
                            ? "Where should we drop off your meals?"
                            : "Secure checkout via Stripe."}
                    </DialogDescription>
                    {/* Progress */}
                    <div className="mt-4 flex items-center gap-2">
                        <div
                            className={`h-1.5 flex-1 rounded-full ${step >= 1 ? "bg-forest" : "bg-mint"}`}
                        />
                        <div
                            className={`h-1.5 flex-1 rounded-full ${step >= 2 ? "bg-forest" : "bg-mint"}`}
                        />
                    </div>
                    <p className="mt-2 text-[11px] uppercase tracking-widest font-bold text-muted2">
                        Step {step} of 2
                    </p>
                </div>

                {/* Scrollable middle region — order summary, upsell, sign-in CTA, form */}
                <div
                    data-testid="checkout-scroll-area"
                    className="flex-1 overflow-y-auto modal-scroll"
                >
                {/* Order summary always at top */}
                <div className="px-6 sm:px-7 pt-5">
                    <div className="bg-mint/50 rounded-2xl p-4">
                        <p className="text-[11px] uppercase tracking-widest font-bold text-forest">
                            Order Summary
                        </p>
                        <ul className="mt-2 space-y-1.5 max-h-32 overflow-y-auto modal-scroll">
                            {summary.lines.map((l) => (
                                <li
                                    key={l.key || l.meal?.id}
                                    className="flex justify-between items-center text-sm text-navy gap-2"
                                >
                                    <span className="truncate pr-2 flex items-center gap-1.5">
                                        {l.kind === "family" && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-burnt/10 text-burnt text-[9px] font-bold tracking-wider uppercase">
                                                Family
                                            </span>
                                        )}
                                        {l.qty}× {l.name || l.meal?.name}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <span className="font-bold tabular-nums">
                                            ${l.line.toFixed(2)}
                                        </span>
                                        {l.kind === "family" && onRemoveFamilyItem && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onRemoveFamilyItem(l.key)
                                                }
                                                data-testid={`remove-family-${l.key}`}
                                                aria-label="Remove family plate"
                                                className="text-muted2 hover:text-burnt text-xs"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-3 pt-3 border-t border-forest/15 space-y-1 text-sm">
                            <div className="flex justify-between text-muted2">
                                <span>Subtotal</span>
                                <span className="tabular-nums">
                                    ${summary.subtotal.toFixed(2)}
                                </span>
                            </div>
                            {volumeDiscount > 0 && (
                                <div
                                    data-testid="checkout-volume-discount"
                                    className="flex justify-between text-forest font-bold"
                                >
                                    <span>
                                        Volume discount · {summary.volumePct}% off
                                        ({summary.singleMealQty} plates)
                                    </span>
                                    <span className="tabular-nums">
                                        −${volumeDiscount.toFixed(2)}
                                    </span>
                                </div>
                            )}
                            {subscribe && subDiscount > 0 && (
                                <div className="flex justify-between text-forest font-bold">
                                    <span>Subscribe & save 10%</span>
                                    <span className="tabular-nums">
                                        −${subDiscount.toFixed(2)}
                                    </span>
                                </div>
                            )}
                            {promo && promoDiscount > 0 && (
                                <div
                                    data-testid="checkout-promo-discount"
                                    className="flex justify-between text-forest font-bold"
                                >
                                    <span>
                                        Promo {promo.code} ({promo.percent_off}% off)
                                    </span>
                                    <span className="tabular-nums">
                                        −${promoDiscount.toFixed(2)}
                                    </span>
                                </div>
                            )}
                            {deliveryQuote?.deliverable !== false && (
                                <div className="flex justify-between text-muted2">
                                    <span>
                                        {deliveryQuote?.zone === "extended"
                                            ? "Extended-zone delivery"
                                            : "Greater Boston delivery"}
                                    </span>
                                    <span className="tabular-nums">
                                        ${resolvedDeliveryFee.toFixed(2)}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between text-navy font-bold pt-1">
                                <span>{subscribe ? "Today's total" : "Total"}</span>
                                <span
                                    className="font-display text-2xl text-forest tabular-nums"
                                    data-testid="checkout-total"
                                >
                                    ${finalTotal.toFixed(2)}
                                </span>
                            </div>
                            {isPreOrder && depositPct < 100 && (
                                <div
                                    data-testid="checkout-deposit-summary"
                                    className="mt-2 pt-2 border-t border-mint flex flex-col gap-1 text-sm"
                                >
                                    <div className="flex justify-between text-forest font-bold">
                                        <span>Deposit today ({depositPct}%)</span>
                                        <span className="tabular-nums">
                                            ${depositAmount.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-muted2">
                                        <span>Balance due {balanceDueText}</span>
                                        <span className="tabular-nums">
                                            ${balanceAmount.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tier upsell hint */}
                    {summary.singleMealQty > 0 && summary.singleMealQty < 12 && (
                        <TierUpsell
                            qty={summary.singleMealQty}
                            singleMealSubtotal={summary.singleMealSubtotal}
                        />
                    )}
                </div>

                {/* Subscribe & Save banner — step 1 only */}
                {step === 1 && (
                    <div className="px-6 sm:px-7 pt-4">
                        {canSubscribe ? (
                            <label
                                data-testid="subscribe-toggle"
                                className={`flex items-start gap-3 p-4 rounded-2xl cursor-pointer transition-all border-2 ${subscribe ? "border-forest bg-mint/70" : "border-mint bg-white hover:border-sage"}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={subscribe}
                                    onChange={(e) => setSubscribe(e.target.checked)}
                                    className="mt-1 w-5 h-5 accent-forest cursor-pointer"
                                    data-testid="subscribe-checkbox"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-navy">
                                            Subscribe & save 10%
                                        </span>
                                        <span className="px-2 py-0.5 rounded-full bg-gold text-navy text-[10px] font-bold uppercase tracking-widest">
                                            Best value
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted2 mt-1 leading-relaxed">
                                        Get this same box delivered every week.
                                        Save{" "}
                                        <span className="font-bold text-forest">
                                            ${(summary.subtotal * 0.1).toFixed(2)}
                                        </span>{" "}
                                        today and on every future delivery.
                                        Pause or cancel anytime from your account.
                                    </p>
                                </div>
                            </label>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    onOpenChange(false);
                                    onAuthRequired && onAuthRequired();
                                }}
                                data-testid="subscribe-signin-cta"
                                className="w-full text-left p-4 rounded-2xl bg-white border-2 border-dashed border-mint hover:border-forest transition-all"
                            >
                                <p className="font-bold text-navy">
                                    Sign in to subscribe & save 10% 🌴
                                </p>
                                <p className="text-xs text-muted2 mt-1">
                                    Members save{" "}
                                    <span className="font-bold text-forest">
                                        ${(summary.subtotal * 0.1).toFixed(2)}
                                    </span>{" "}
                                    on this box. Tap here to sign in.
                                </p>
                            </button>
                        )}
                    </div>
                )}

                <div className="px-6 sm:px-7 py-5">
                    {step === 1 ? (
                        <div className="space-y-4">
                            <Field label="Full name">
                                <input
                                    data-testid="checkout-name"
                                    value={form.name}
                                    onChange={upd("name")}
                                    placeholder="Marie Dupont"
                                    className="w-full px-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition"
                                />
                            </Field>
                            <Field label="Email">
                                <input
                                    data-testid="checkout-email"
                                    value={form.email}
                                    onChange={upd("email")}
                                    type="email"
                                    placeholder="marie@example.com"
                                    className="w-full px-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition"
                                />
                            </Field>
                            <Field label="Phone">
                                <input
                                    data-testid="checkout-phone"
                                    value={form.phone}
                                    onChange={upd("phone")}
                                    placeholder="(617) 555-0143"
                                    className="w-full px-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition"
                                />
                            </Field>
                            <Field label="Delivery address (Greater Boston)">
                                <input
                                    data-testid="checkout-address"
                                    value={form.address}
                                    onChange={upd("address")}
                                    placeholder="123 Blue Hill Ave, Boston, MA 02124"
                                    className="w-full px-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition"
                                />
                                {form.address.trim().length >= 8 && (
                                    <div
                                        data-testid="delivery-quote"
                                        className="mt-2 text-xs"
                                    >
                                        {deliveryBusy && (
                                            <span className="text-muted2">
                                                Checking delivery range from Woburn, MA…
                                            </span>
                                        )}
                                        {!deliveryBusy && deliveryQuote && deliveryQuote.deliverable && (
                                            <span className="text-forest font-bold">
                                                ✓ {deliveryQuote.distance_miles ?? "—"} mi from Woburn ·{" "}
                                                {deliveryQuote.zone === "extended"
                                                    ? `Extended-zone delivery $${deliveryQuote.delivery_fee?.toFixed(2)}`
                                                    : deliveryQuote.zone === "unknown"
                                                    ? `Delivery $${deliveryQuote.delivery_fee?.toFixed(2)} (verify on confirmation)`
                                                    : `Local-zone delivery $${deliveryQuote.delivery_fee?.toFixed(2)}`}
                                            </span>
                                        )}
                                        {!deliveryBusy && deliveryQuote && deliveryQuote.deliverable === false && (
                                            <span
                                                data-testid="delivery-out-of-range"
                                                className="text-burnt font-bold"
                                            >
                                                ✗ {deliveryQuote.distance_miles ?? "?"} mi from Woburn —
                                                outside our 35-mile delivery range. Email{" "}
                                                <a href="mailto:caribbeanmealp@gmail.com" className="underline">
                                                    caribbeanmealp@gmail.com
                                                </a>{" "}
                                                for an exception.
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Field>
                            <Field label="Delivery day">
                                <div className="rounded-2xl bg-mint/30 border border-mint p-3 mb-3 text-[12.5px] text-navy leading-snug">
                                    <span className="font-bold">
                                        We deliver on Fridays &amp; Saturdays.
                                    </span>{" "}
                                    Order by Thursday 10 PM for Friday delivery,
                                    or by Friday 10 PM for Saturday. Delivery
                                    window: 11 AM – 8 PM.
                                </div>
                                <div
                                    data-testid="checkout-delivery-slots"
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                                >
                                    {slots.map((slot) => {
                                        const active =
                                            form.delivery_date === slot.iso;
                                        return (
                                            <button
                                                key={slot.iso}
                                                type="button"
                                                data-testid={`checkout-slot-${slot.dayKind}`}
                                                onClick={() =>
                                                    handleSlotPick(slot.iso)
                                                }
                                                className={`relative text-left rounded-2xl border-2 p-4 transition-all ${
                                                    active
                                                        ? "border-forest bg-white shadow-[0_8px_22px_rgba(13,40,24,0.18)]"
                                                        : "border-mint bg-white hover:border-forest/60"
                                                }`}
                                            >
                                                {active && (
                                                    <span className="absolute top-2.5 right-2.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-forest text-white">
                                                        <Check
                                                            size={12}
                                                            strokeWidth={3}
                                                        />
                                                    </span>
                                                )}
                                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-forest">
                                                    <Truck size={12} />
                                                    {slot.label}
                                                </div>
                                                <p className="mt-1.5 font-serif font-bold text-navy text-lg leading-tight">
                                                    {slot.dateText}
                                                </p>
                                                <p className="text-xs text-muted2 mt-0.5">
                                                    {slot.window}
                                                </p>
                                                <p className="mt-2 text-[11px] text-burnt font-bold">
                                                    {slot.cutoffText}
                                                </p>
                                                {slot.isPreOrder && (
                                                    <span
                                                        data-testid={`slot-preorder-pill-${slot.dayKind}`}
                                                        className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/90 text-navy text-[9px] font-black uppercase tracking-widest"
                                                    >
                                                        Pre-order
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>

                            {/* Pre-order deposit plan */}
                            {isPreOrder && (
                                <Field label="Payment plan">
                                    <div
                                        data-testid="checkout-deposit-plan"
                                        className="rounded-2xl bg-gold/10 border-2 border-gold/40 p-4"
                                    >
                                        <p className="text-[11px] uppercase tracking-widest font-bold text-burnt mb-1">
                                            🇭🇹 Pre-order — choose how to pay
                                        </p>
                                        <p className="text-sm text-navy mb-3 leading-snug">
                                            You're booking <b>{selectedSlot?.dateText}</b>.
                                            Lock in your slot with a deposit now —
                                            balance due by <b>{balanceDueText}</b>.
                                            Two payment reminders, then forfeit on
                                            non-payment (see Refund Policy).
                                        </p>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            {[10, 25, 50, 100].map((pct) => {
                                                const amt = +(finalTotal * (pct / 100)).toFixed(2);
                                                const active = depositPct === pct;
                                                return (
                                                    <button
                                                        key={pct}
                                                        type="button"
                                                        data-testid={`deposit-option-${pct}`}
                                                        onClick={() => setDepositPct(pct)}
                                                        className={`rounded-xl border-2 p-2.5 text-left transition-all ${
                                                            active
                                                                ? "border-forest bg-white shadow-[0_4px_14px_rgba(13,40,24,0.16)]"
                                                                : "border-mint bg-white/70 hover:border-forest/60"
                                                        }`}
                                                    >
                                                        <div className="text-[10px] font-bold tracking-widest text-forest uppercase">
                                                            {pct === 100 ? "Pay in full" : `${pct}% deposit`}
                                                        </div>
                                                        <div className="font-serif font-black text-navy text-base mt-0.5">
                                                            ${amt.toFixed(2)}
                                                        </div>
                                                        {pct < 100 && (
                                                            <div className="text-[10px] text-muted2 mt-0.5">
                                                                ${(finalTotal - amt).toFixed(2)} later
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </Field>
                            )}
                            <Field label="Notes (optional)">
                                <textarea
                                    data-testid="checkout-notes"
                                    value={form.notes}
                                    onChange={upd("notes")}
                                    rows={2}
                                    placeholder="Leave at the front desk, ring buzzer 3B…"
                                    className="w-full px-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition resize-none"
                                />
                            </Field>

                            {/* Promo code */}
                            <Field label="Promo code (optional)">
                                {promo ? (
                                    <div
                                        data-testid="checkout-promo-applied"
                                        className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-mint/60 border-2 border-forest"
                                    >
                                        <span className="flex items-center gap-2 text-navy">
                                            <Tag size={16} className="text-forest" />
                                            <span className="font-mono font-bold tracking-widest">
                                                {promo.code}
                                            </span>
                                            <span className="text-xs text-muted2">
                                                · {promo.percent_off}% off subtotal
                                            </span>
                                        </span>
                                        <button
                                            type="button"
                                            data-testid="checkout-promo-remove"
                                            onClick={clearPromo}
                                            className="text-muted2 hover:text-burnt transition-colors"
                                            aria-label="Remove promo code"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex gap-2">
                                            <input
                                                data-testid="checkout-promo-input"
                                                value={promoInput}
                                                onChange={(e) =>
                                                    setPromoInput(
                                                        e.target.value.toUpperCase(),
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        applyPromo();
                                                    }
                                                }}
                                                placeholder="WELCOME10"
                                                className="flex-1 px-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition font-mono tracking-widest uppercase"
                                            />
                                            <button
                                                type="button"
                                                data-testid="checkout-promo-apply"
                                                onClick={applyPromo}
                                                disabled={
                                                    promoBusy ||
                                                    !promoInput.trim()
                                                }
                                                className="px-5 rounded-xl bg-forest text-white font-bold text-sm hover:bg-forestDark disabled:opacity-50 transition"
                                            >
                                                {promoBusy ? "…" : "Apply"}
                                            </button>
                                        </div>
                                        {promoErr && (
                                            <p
                                                data-testid="checkout-promo-error"
                                                className="mt-2 text-xs text-burnt"
                                            >
                                                {promoErr}
                                            </p>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-white rounded-2xl p-4 border border-mint">
                                <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                                    Delivering to
                                </p>
                                <p className="mt-1 font-bold text-navy">
                                    {form.name}
                                </p>
                                <p className="text-sm text-muted2">
                                    {form.address}
                                </p>
                                <p className="text-sm text-muted2">
                                    {form.email} · {form.phone}
                                </p>
                                <p className="mt-2 text-sm text-forest font-bold">
                                    📦 {prettyDate(form.delivery_date)}
                                </p>
                            </div>

                            <div className="bg-white rounded-2xl p-4 border border-mint">
                                <p className="text-[11px] uppercase tracking-widest font-bold text-muted2 mb-2">
                                    Payment method
                                </p>
                                <div className="flex items-center gap-2">
                                    <PayBadge>VISA</PayBadge>
                                    <PayBadge>MC</PayBadge>
                                    <PayBadge>AMEX</PayBadge>
                                    <PayBadge> Pay</PayBadge>
                                </div>
                                <p className="mt-3 text-xs text-muted2 leading-relaxed">
                                    You'll be redirected to Stripe's secure
                                    checkout to enter your card. We accept all
                                    major credit and debit cards.
                                </p>
                            </div>

                            <div className="flex items-center gap-2 text-xs text-muted2">
                                <Lock size={14} className="text-forest" />
                                Payments are encrypted and secure.
                            </div>
                        </div>
                    )}
                </div>
                {/* /scroll area */}
                </div>

                <div className="px-6 sm:px-7 py-4 bg-white border-t border-mint flex items-center justify-between gap-3 shrink-0">
                    {step === 1 ? (
                        <>
                            <Button
                                data-testid="checkout-cancel"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                className="rounded-full text-muted2 hover:text-navy"
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="checkout-continue-btn"
                                disabled={!formValid}
                                onClick={() => setStep(2)}
                                className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-6 py-5 disabled:opacity-50"
                            >
                                Continue to Payment{" "}
                                <ArrowRight size={16} className="ml-1" />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                data-testid="checkout-back"
                                variant="ghost"
                                onClick={() => setStep(1)}
                                className="rounded-full text-muted2 hover:text-navy"
                            >
                                <ArrowLeft size={16} className="mr-1" /> Back
                            </Button>
                            <Button
                                data-testid="checkout-pay-btn"
                                disabled={loading}
                                onClick={goPay}
                                className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-6 py-5 disabled:opacity-50"
                            >
                                <Lock size={14} className="mr-2" />
                                {loading
                                    ? "Redirecting…"
                                    : isPreOrder && depositPct < 100
                                    ? `Pay $${depositAmount.toFixed(2)} deposit`
                                    : `Pay $${finalTotal.toFixed(2)}`}
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                {label}
            </span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}

function PayBadge({ children }) {
    return (
        <span className="px-2.5 py-1 rounded-md bg-navy text-white text-[10px] font-bold tracking-wider">
            {children}
        </span>
    );
}


function TierUpsell({ qty, singleMealSubtotal }) {
    // Map current qty to the next tier
    const next = qty < 6 ? 6 : qty < 9 ? 9 : qty < 12 ? 12 : null;
    if (!next) return null;
    const nextPct = next === 6 ? 10 : next === 9 ? 15 : 20;
    const needed = next - qty;
    // Estimate what they'd save at next tier using current avg unit price
    const avg = qty > 0 ? singleMealSubtotal / qty : 0;
    const projectedSubtotal = +(singleMealSubtotal + avg * needed).toFixed(2);
    const projectedSavings = +(projectedSubtotal * (nextPct / 100)).toFixed(2);
    return (
        <div
            data-testid="checkout-tier-upsell"
            className="mt-3 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3 flex items-start gap-3"
        >
            <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gold text-navy font-bold text-sm">
                +{needed}
            </span>
            <div className="text-sm leading-snug text-navy">
                <p className="font-bold">
                    Add {needed} more {needed === 1 ? "plate" : "plates"} to
                    unlock {nextPct}% off
                </p>
                <p className="text-muted2 text-[13px]">
                    You'd save about ${projectedSavings.toFixed(2)} at the{" "}
                    {next}-plate tier. Mix &amp; match any meals.
                </p>
            </div>
        </div>
    );
}
